import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Publish as PublishIcon,
  Code as CodeIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

import { nodeEditorService } from '../../services/nodeEditorService';
import { NodeEditorSession } from '../../types/nodeEditor.types';
import { ChatPanel } from './ChatPanel';
import { LayoutEditor } from './LayoutEditor';
import { CodeEditor } from './CodeEditor';
import { SettingsPanel } from './SettingsPanel';
import { PromptManager } from './PromptManager';
import { SessionList } from './SessionList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, index, value }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`editor-tabpanel-${index}`}
      aria-labelledby={`editor-tab-${index}`}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && children}
    </div>
  );
};

export const NodeEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId?: string }>();
  
  // State
  const [session, setSession] = useState<NodeEditorSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // New session form
  const [newSessionData, setNewSessionData] = useState({
    node_id: '',
    node_name: '',
    node_display_name: '',
    node_category: 'action' as 'trigger' | 'action' | 'core'
  });
  
  // Publish form
  const [publishData, setPublishData] = useState({
    title: '',
    description: '',
    is_public: false
  });
  
  // Auto-save ref
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<Date | null>(null);
  
  // Load session on mount or sessionId change
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    } else {
      setSession(null);
      setActiveTab(0);
    }
  }, [sessionId]);
  
  // Auto-save setup
  useEffect(() => {
    if (session && session.auto_save_enabled) {
      autoSaveIntervalRef.current = setInterval(() => {
        handleAutoSave();
      }, session.auto_save_interval * 1000);
    }
    
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [session]);
  
  const loadSession = async (id: string) => {
    setLoading(true);
    try {
      const sessionData = await nodeEditorService.getSession(id);
      setSession(sessionData);
      setPublishData(prev => ({
        ...prev,
        title: sessionData.node_display_name || sessionData.node?.display_name || 'New Node'
      }));
    } catch (error) {
      showSnackbar('Failed to load session', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAutoSave = async () => {
    if (!session) return;
    
    // Only auto-save if there are unsaved changes (simplified check)
    const now = new Date();
    if (lastSavedRef.current && now.getTime() - lastSavedRef.current.getTime() < 5000) {
      return; // Don't save more frequently than every 5 seconds
    }
    
    try {
      await handleSave();
      lastSavedRef.current = new Date();
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };
  
  const handleCreateSession = async () => {
    try {
      const internalName = newSessionData.node_name.trim()
        || newSessionData.node_display_name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
        || `node_${Date.now()}`;

      if (!internalName) {
        showSnackbar('Please enter Node Name or Display Name', 'error');
        return;
      }

      const payload = {
        node_name: internalName,
        node_display_name: newSessionData.node_display_name.trim(),
        node_category: newSessionData.node_category,
      };
      const newSession = await nodeEditorService.createSession(payload);
      setShowNewSessionDialog(false);
      navigate(`/app/node-editor/${newSession.id}`);
      showSnackbar('New session created', 'success');
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.error || error?.message;
      showSnackbar(detail || 'Failed to create session', 'error');
    }
  };
  
  const handleSave = async () => {
    if (!session) return;
    
    try {
      await nodeEditorService.saveSession(session.id, {
        frontend_code: session.frontend_code,
        backend_code: session.backend_code,
        component_layout: session.component_layout,
        chat_history: session.chat_history
      });
      showSnackbar('Session saved', 'success');
    } catch (error) {
      showSnackbar('Failed to save session', 'error');
      throw error;
    }
  };
  
  const handlePublish = async () => {
    if (!session) return;
    
    try {
      const result = await nodeEditorService.publishSession(session.id, publishData);
      setShowPublishDialog(false);
      showSnackbar(`Published successfully! URL: ${result.published_url}`, 'success');
      loadSession(session.id);
    } catch (error) {
      showSnackbar('Failed to publish', 'error');
    }
  };
  
  const handleGenerateFrontend = async (requirements?: string) => {
    if (!session) return;
    
    setLoading(true);
    try {
      const result = await nodeEditorService.generateFrontend(session.id, requirements);
      setSession(prev => prev ? { ...prev, frontend_code: result.frontend_code } : null);
      showSnackbar('Frontend code generated', 'success');
    } catch (error) {
      showSnackbar('Failed to generate frontend', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateBackend = async (requirements?: string) => {
    if (!session) return;
    
    setLoading(true);
    try {
      const result = await nodeEditorService.generateBackend(session.id, requirements);
      setSession(prev => prev ? { ...prev, backend_code: result.backend_code } : null);
      showSnackbar('Backend code generated', 'success');
    } catch (error) {
      showSnackbar('Failed to generate backend', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoGenerateLayout = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const result = await nodeEditorService.autoLayout(session.id);
      const nextLayout = result?.layout_data ? result.layout_data : result;
      setSession(prev => prev ? { ...prev, component_layout: nextLayout } : null);
      showSnackbar('Layout auto-generated', 'success');
    } catch (error) {
      showSnackbar('Failed to auto-generate layout', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReactFromLayout = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const result = await nodeEditorService.generateReact(session.id);
      const generatedCode = result?.frontend_code || '';
      setSession(prev => prev ? { ...prev, frontend_code: generatedCode } : null);
      setActiveTab(2);
      showSnackbar('React code generated from layout', 'success');
    } catch (error) {
      showSnackbar('Failed to generate React code', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChat = async (message: string) => {
    if (!session) return;
    
    try {
      const result = await nodeEditorService.chat(session.id, message);
      
      // Add messages to chat history
      const newHistory: NodeEditorSession['chat_history'] = [
        ...session.chat_history,
        { role: 'user', content: message, timestamp: new Date().toISOString() },
        { role: 'assistant', content: String(result.response ?? ''), timestamp: new Date().toISOString() }
      ];
      
      setSession(prev => prev ? { ...prev, chat_history: newHistory } : null);
      return result.response;
    } catch (error) {
      showSnackbar('Chat failed', 'error');
      throw error;
    }
  };
  
  const handleLayoutChange = (layout: any) => {
    if (!session) return;
    setSession(prev => prev ? { ...prev, component_layout: layout } : null);
  };
  
  const handleCodeChange = (type: 'frontend' | 'backend', code: string) => {
    if (!session) return;
    setSession(prev => prev ? { ...prev, [`${type}_code`]: code } : null);
  };
  
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  // Render main content
  const renderMainContent = () => {
    if (!sessionId) {
      return (
        <SessionList
          onSelectSession={(id) =>
            navigate(id === 'new' ? '/app/node-editor' : `/app/node-editor/${id}`)
          }
        />
      );
    }
    
    if (loading && !session) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (!session) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="textSecondary">
            Session not found
          </Typography>
          <Button variant="contained" onClick={() => navigate('/app/node-editor')} sx={{ mt: 2 }}>
            Back to Sessions
          </Button>
        </Box>
      );
    }
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Session header */}
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/app/node-editor')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">
                {session.node_display_name || session.node?.display_name || 'New Node'}
              </Typography>
              <Chip 
                label={session.status} 
                size="small" 
                color={session.is_published ? 'success' : 'default'}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Save">
                <IconButton onClick={handleSave} disabled={loading}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Publish">
                <IconButton onClick={() => setShowPublishDialog(true)} disabled={loading}>
                  <PublishIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Layout (WYSIWYG)" icon={<DashboardIcon />} iconPosition="start" />
          <Tab label="Chat with LLM" icon={<ChatIcon />} iconPosition="start" />
          <Tab label="Frontend Code" icon={<CodeIcon />} iconPosition="start" />
          <Tab label="Backend Code" icon={<CodeIcon />} iconPosition="start" />
          <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Prompts" icon={<AutoFixIcon />} iconPosition="start" />
        </Tabs>
        
        {/* Tab panels */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <TabPanel value={activeTab} index={0}>
            <LayoutEditor 
              layout={session.component_layout}
              nodeData={session.node_data || session.node}
              onChange={handleLayoutChange}
              onAutoGenerate={handleAutoGenerateLayout}
              onGenerateReact={handleGenerateReactFromLayout}
            />
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            <ChatPanel 
              chatHistory={session.chat_history}
              onSendMessage={handleChat}
              nodeName={session.node_display_name || session.node?.display_name}
            />
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            <CodeEditor 
              code={session.frontend_code}
              language="typescript"
              onChange={(code) => handleCodeChange('frontend', code)}
              onGenerate={() => handleGenerateFrontend()}
              title="Frontend (React + MUI)"
            />
          </TabPanel>
          
          <TabPanel value={activeTab} index={3}>
            <CodeEditor 
              code={session.backend_code}
              language="python"
              onChange={(code) => handleCodeChange('backend', code)}
              onGenerate={() => handleGenerateBackend()}
              title="Backend (Python/Django)"
            />
          </TabPanel>
          
          <TabPanel value={activeTab} index={4}>
            <SettingsPanel session={session} />
          </TabPanel>
          
          <TabPanel value={activeTab} index={5}>
            <PromptManager />
          </TabPanel>
        </Box>
      </Box>
    );
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">WG Node Editor</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<DashboardIcon />} onClick={() => navigate('/app/node-editor')}>
              All Sessions
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowNewSessionDialog(true)}>
              New Node
            </Button>
            {session && (
              <>
                <Button variant="outlined" startIcon={<SaveIcon />} onClick={handleSave} disabled={loading}>
                  Save
                </Button>
                <Button variant="outlined" startIcon={<PublishIcon />} onClick={() => setShowPublishDialog(true)} disabled={loading}>
                  Publish
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        {renderMainContent()}
      </Box>
      
      {/* New Session Dialog */}
      <Dialog open={showNewSessionDialog} onClose={() => setShowNewSessionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Node Session</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Node Category"
            value={newSessionData.node_category}
            onChange={(e) => setNewSessionData(prev => ({ ...prev, node_category: e.target.value as any }))}
            margin="normal"
          >
            <MenuItem value="trigger">Trigger</MenuItem>
            <MenuItem value="action">Action</MenuItem>
            <MenuItem value="core">Core</MenuItem>
          </TextField>
          
          <TextField
            fullWidth
            label="Node Name (internal)"
            value={newSessionData.node_name}
            onChange={(e) => setNewSessionData(prev => ({ ...prev, node_name: e.target.value }))}
            margin="normal"
            placeholder="e.g., myCustomNode"
          />
          
          <TextField
            fullWidth
            label="Display Name"
            value={newSessionData.node_display_name}
            onChange={(e) => setNewSessionData(prev => ({ ...prev, node_display_name: e.target.value }))}
            margin="normal"
            placeholder="e.g., My Custom Node"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewSessionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSession}
            variant="contained"
            disabled={!newSessionData.node_name.trim() && !newSessionData.node_display_name.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Publish Dialog */}
      <Dialog open={showPublishDialog} onClose={() => setShowPublishDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish Node Page</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Page Title"
            value={publishData.title}
            onChange={(e) => setPublishData(prev => ({ ...prev, title: e.target.value }))}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Description"
            value={publishData.description}
            onChange={(e) => setPublishData(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={publishData.is_public}
                onChange={(e) => setPublishData(prev => ({ ...prev, is_public: e.target.checked }))}
              />
            }
            label="Make publicly accessible"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPublishDialog(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained" color="primary">
            Publish
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NodeEditorPage;
